import React from 'react';

import { Divider, Group, Paper, SimpleGrid, Stack, Text, Title } from '@mantine/core';
import { IconCreditCard, IconTag } from '@tabler/icons-react';
import { useLocalization } from 'context/localization-context';
import { BillingPlanRetrieve, BillingPlanWithInstallments, DiscountRetrieve, GymTitle } from 'types';
import { sortInstallmentsByNumber } from 'utils/billing.helpers';
import { formatDiscountValue } from 'utils/discount.helpers';

interface IBillingPlanSummaryProps {
  gym: GymTitle | undefined;
  billingPlan: BillingPlanRetrieve | BillingPlanWithInstallments;
  taxRate: number;
  appliedDiscount?: DiscountRetrieve | null;
}

const BillingPlanSummary = ({
  gym,
  billingPlan,
  taxRate,
  appliedDiscount,
}: IBillingPlanSummaryProps): JSX.Element => {
  const { copy, formatCurrency, calculateInvoiceItemValues, calculateInvoiceSummary } = useLocalization(
    gym?.country,
    {
      isTaxInclusive: gym?.isTaxInclusive,
    },
  );

  const discountValue = appliedDiscount
    ? appliedDiscount.valueType === 'PERCENTAGE'
      ? Number(appliedDiscount.percentage ?? 0)
      : appliedDiscount.amount ?? 0
    : undefined;
  const discountType = appliedDiscount?.valueType;
  const taxRateValue = taxRate * 100;

  const summaryItems = billingPlan.installments.map(installment => ({
    itemPrice: installment.price,
    quantity: 1,
    taxRateValue,
    discountValue: installment.installmentNumber === 1 ? discountValue : undefined,
    discountType: installment.installmentNumber === 1 ? discountType : undefined,
  }));

  const { total, subtotal, taxAmount } = calculateInvoiceSummary(summaryItems);

  const plural = billingPlan.numberOfInstallments > 1;

  return (
    <Paper shadow='xs' radius='lg' withBorder p='sm'>
      <Group noWrap spacing='xs'>
        <IconCreditCard />
        <Title order={3} fw={600} truncate>
          {billingPlan.name}
        </Title>
      </Group>
      <Stack spacing={3} mt='md'>
        <Text color='dark.8' fw='500'>
          {`Frequency: 
              ${
                plural
                  ? `Every ${
                      (billingPlan.installmentIntervalCount || 0) > 1
                        ? `${billingPlan.installmentIntervalCount} ${billingPlan.installmentInterval}s`
                        : billingPlan.installmentInterval
                    }`
                  : 'Once'
              }`}
        </Text>
        <Text color='dark.8' fw='500'>
          {`Installments ${billingPlan.numberOfInstallments}`}
        </Text>

        {sortInstallmentsByNumber(billingPlan.installments).map(installment => {
          const isFirst = installment.installmentNumber === 1;
          const itemValues = calculateInvoiceItemValues({
            itemPrice: installment.price,
            quantity: 1,
            taxRateValue,
            discountValue: isFirst ? discountValue : undefined,
            discountType: isFirst ? discountType : undefined,
          });
          const isDiscounted = itemValues.discountAmount > 0;
          return (
            <React.Fragment key={installment.installmentId}>
              <Group position='apart'>
                <Text color='dark.8' fw='300'>
                  {`Installment ${installment.installmentNumber}:`}
                </Text>
                <Group spacing='xs'>
                  {isDiscounted && (
                    <Text color='dimmed' fw='300' strikethrough>
                      {formatCurrency(installment.price / 100)}
                    </Text>
                  )}
                  <Text color={isDiscounted ? 'green' : 'dark.8'} fw='300'>
                    {formatCurrency(itemValues.total / 100)}
                  </Text>
                </Group>
              </Group>

              {isFirst && appliedDiscount && (
                <SimpleGrid cols={2} w='100%'>
                  <Group align='center' spacing='xs'>
                    <IconTag size='1.4rem' color='#4AC360' />
                    <Text size='sm' color='green' weight={600}>
                      Discount: {appliedDiscount.name} {formatDiscountValue(appliedDiscount, formatCurrency)}
                    </Text>
                  </Group>
                  <Text size='sm' color='green' fw='300' align='end'>
                    -{formatCurrency(itemValues.discountAmount / 100)}
                  </Text>
                </SimpleGrid>
              )}
            </React.Fragment>
          );
        })}

        <Divider my='xs' />

        <Group position='apart'>
          <Text color='dark.8' fw='500'>
            {copy.financial.subtotal}
          </Text>
          <Text color='dark.8' fw='500'>
            {formatCurrency(subtotal / 100)}
          </Text>
        </Group>

        <Group position='apart'>
          <Text color='dark.8' fw='500'>
            {copy.financial.tax}
          </Text>
          <Text color='dark.8' fw='500'>
            {formatCurrency(taxAmount / 100)}
          </Text>
        </Group>

        <Group position='apart'>
          <Title order={3} fw={600}>
            Total:
          </Title>
          <Title order={3} fw={600}>
            {formatCurrency(total / 100)}
          </Title>
        </Group>
      </Stack>
    </Paper>
  );
};

export default BillingPlanSummary;
